import type { NavItemIdentifier, NavFavoritesContextType } from 'src/types/nav-favorites';

import { useMemo, useState, useEffect, useContext, createContext } from 'react';

// ----------------------------------------------------------------------

const STORAGE_KEY = 'nav-favorites';

const NavFavoritesContext = createContext<NavFavoritesContextType | undefined>(undefined);

// ----------------------------------------------------------------------

type NavFavoritesProviderProps = {
  children: React.ReactNode;
};

export function NavFavoritesProvider({ children }: NavFavoritesProviderProps) {
  // Cargar favoritos desde localStorage
  const [favorites, setFavorites] = useState<NavItemIdentifier[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return [];
    }
  });

  // Guardar favoritos en localStorage cuando cambien
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }, [favorites]);

  // Función para agregar un item a favoritos
  const addToFavorites = (item: NavItemIdentifier) => {
    setFavorites((prev) => {
      // Verificar si ya existe
      const exists = prev.some(
        (fav) => fav.path === item.path && fav.parentPath === item.parentPath
      );
      if (exists) return prev;
      return [...prev, item];
    });
  };

  // Función para eliminar un item de favoritos
  const removeFromFavorites = (item: NavItemIdentifier) => {
    setFavorites((prev) =>
      prev.filter((fav) => !(fav.path === item.path && fav.parentPath === item.parentPath))
    );
  };

  // Función para verificar si un item es favorito
  const isItemFavorite = (item: NavItemIdentifier) =>
    favorites.some((fav) => fav.path === item.path && fav.parentPath === item.parentPath);

  // Función para alternar el estado de favorito de un item
  const toggleFavorite = (item: NavItemIdentifier) => {
    if (isItemFavorite(item)) {
      removeFromFavorites(item);
    } else {
      addToFavorites(item);
    }
  };

  const value = useMemo(
    () => ({
      favorites,
      addToFavorites,
      removeFromFavorites,
      isItemFavorite,
      toggleFavorite,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favorites]
  );

  return <NavFavoritesContext.Provider value={value}>{children}</NavFavoritesContext.Provider>;
}

// ----------------------------------------------------------------------

export function useNavFavorites() {
  const context = useContext(NavFavoritesContext);

  if (!context) {
    throw new Error('useNavFavorites must be used within a NavFavoritesProvider');
  }

  return context;
}